import TabsHeader from 'components/common/form/tab/TabsHeader';
import FormTopControlPanel from 'components/common/form/top-control-panel/FormTopControlPanel';
import SharedControls from 'components/common/form/shared-controls/SharedControls';
import React, { useEffect, useRef } from 'react';
import FormPageHeader from 'components/common/form/renderer/page/FormPageHeader';
import FormOnPageSaveButton from 'components/common/form/renderer/page/FormOnPageSaveButton';
import { IFormLayoutProps } from 'components/common/form/renderer/FormLayoutComposer';
import { FormLayoutContext } from 'components/common/form/layout/tab';
import FormTabsContent from 'components/common/form/layout/tab/FormTabsContent';
import PanelBackButton from 'components/common/form/panel-back-button/PanelBackButton';
import { Box, Container } from '@mui/material';
import { getFormId } from 'components/common/form/formTools';
import StaticInfo from 'components/common/StaticInfo';

interface IFormPageLayoutProps extends Omit<IFormLayoutProps, 'confirmPopupProps' | 'subFormPopup'> {}

export default function FormPageLayout({
    elementProps,
    tabs,
    t,
    sharedComponents,
    panelSettings,
    hookForm,
    componentsMap,
    customTabs,
    isNew,
    uid,
}: IFormPageLayoutProps) {
    const tabsHeader = (
        <TabsHeader formActions={hookForm.form} tabs={tabs.formTabs} onChange={tabs.handleTabChange} t={t} />
    );
    const formTopControlPanel = <FormTopControlPanel panelSettings={panelSettings} formActions={hookForm.form} />;
    const panelBackButton = <PanelBackButton settings={panelSettings?.backButton} />;
    const id = getFormId(uid, isNew);
    const formRef = useRef<any>(null);
    const scrollContainerRef = useRef<any>(null);
    const userInteractedRef = useRef<boolean>(false);

    useEffect(() => {
        if (isNew) {
            scrollContainerRef.current = formRef.current.querySelector('.default-components');
        }
    }, []);

    useEffect(() => {
        if (!isNew || !scrollContainerRef.current) return;

        //EXPERIMENTAL: scroll to new appear fields on New page
        const visibleFormComponents = new Set<Element>();
        let isInitialized = false;

        const initializeVisibleComponents = () => {
            if (isInitialized) return;
            isInitialized = true;

            scrollContainerRef.current
                .querySelectorAll('.form-component-holder:not(.d-none)')
                .forEach((el: Element) => {
                    visibleFormComponents.add(el);
                });
        };

        const enableScrollOnInteraction = () => {
            initializeVisibleComponents();
            userInteractedRef.current = true;
            scrollContainerRef.current.removeEventListener('click', enableScrollOnInteraction);
            scrollContainerRef.current.removeEventListener('focusin', enableScrollOnInteraction);
        };

        scrollContainerRef.current.addEventListener('click', enableScrollOnInteraction);
        scrollContainerRef.current.addEventListener('focusin', enableScrollOnInteraction);

        const scrollToNewFormComponent = (formComponent: Element) => {
            const containerRect = scrollContainerRef.current.getBoundingClientRect();
            const nodeRect = formComponent.getBoundingClientRect();
            const extraPadding = 24;
            const minVisibleHeight = 100;

            const visibleTop = Math.max(nodeRect.top, containerRect.top);
            const visibleBottom = Math.min(nodeRect.bottom, containerRect.bottom);
            const visibleHeight = Math.max(0, visibleBottom - visibleTop);

            const isEnoughVisible =
                visibleHeight >= Math.min(minVisibleHeight, nodeRect.height) &&
                nodeRect.bottom + extraPadding <= containerRect.bottom;

            if (!isEnoughVisible) {
                const scrollTop = scrollContainerRef.current.scrollTop;
                const targetScrollTop = scrollTop + (nodeRect.bottom + extraPadding - containerRect.bottom);

                scrollContainerRef.current.scrollTo({
                    top: targetScrollTop,
                    behavior: 'smooth',
                });
            }
        };

        let checkTimeout: ReturnType<typeof setTimeout> | null = null;

        const checkForNewVisibleComponents = () => {
            const currentVisibleComponents = scrollContainerRef.current.querySelectorAll(
                '.form-component-holder:not(.d-none)',
            );
            const currentVisibleSet = new Set(currentVisibleComponents);

            for (const component of visibleFormComponents) {
                if (!currentVisibleSet.has(component)) {
                    visibleFormComponents.delete(component);
                }
            }

            for (const formComponent of currentVisibleComponents) {
                if (!visibleFormComponents.has(formComponent)) {
                    visibleFormComponents.add(formComponent);
                    scrollToNewFormComponent(formComponent);
                    return;
                }
            }
        };

        const observer = new MutationObserver((mutations) => {
            if (!userInteractedRef.current) return;

            const hasRelevantMutation = mutations.some(
                (m) =>
                    m.type === 'attributes' &&
                    m.attributeName === 'class' &&
                    m.target instanceof HTMLElement &&
                    m.target.classList.contains('form-component-holder'),
            );

            if (hasRelevantMutation) {
                checkForNewVisibleComponents();
            } else {
                if (checkTimeout) clearTimeout(checkTimeout);
                checkTimeout = setTimeout(checkForNewVisibleComponents, 50);
            }
        });

        observer.observe(scrollContainerRef.current, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'style'],
        });

        return () => {
            observer.disconnect();
            if (checkTimeout) clearTimeout(checkTimeout);
            scrollContainerRef.current?.removeEventListener('click', enableScrollOnInteraction);
            scrollContainerRef.current?.removeEventListener('focusin', enableScrollOnInteraction);
        };
    }, [isNew]);

    return (
        <form
            autoComplete={'off'}
            id={id}
            onSubmit={hookForm.form.hookFormHandleSubmit(function (data) {
                return hookForm.form.formAct.act.submit.exec(data);
            })}
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
            ref={formRef}
        >
            {!isNew &&
                (panelSettings?.hidden ? (
                    <span className="d-none">{formTopControlPanel}</span>
                ) : (
                    <FormPageHeader
                        tabsHeader={tabsHeader}
                        formTopControlPanel={formTopControlPanel}
                        panelBackButton={panelBackButton}
                    />
                ))}
            {/*ToDo: Refactor Add New element through Page, not popup*/}
            <Box
                sx={{
                    my: isNew ? 4 : undefined,
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    overflow: 'hidden',
                }}
            >
                {sharedComponents.length > 0 && (
                    <SharedControls elementProps={elementProps} components={sharedComponents}></SharedControls>
                )}
                {hookForm.form.hookFormState.errors.__general && (
                    <Container
                        className={'mi-container-3 form-container--maxfull'}
                        maxWidth={false}
                        component="main"
                        disableGutters={true}
                    >
                        <StaticInfo
                            label={hookForm.form.hookFormState.errors.__general?.message as string}
                            align={'center'}
                            type={'error'}
                        />
                    </Container>
                )}
                <FormLayoutContext.Provider value={{ formLayout: 'page', formType: isNew ? 'new' : 'edit' }}>
                    <FormTabsContent
                        componentsMap={componentsMap}
                        formTabs={tabs.formTabs}
                        elementProps={elementProps}
                        customTabs={customTabs}
                    />
                </FormLayoutContext.Provider>
                {isNew && <FormOnPageSaveButton hookForm={hookForm} />}
            </Box>
        </form>
    );
}
